/**
 * PIModeledBanner Component
 * Shows a banner when modeled PI score is enabled, allowing users to toggle it off
 */

import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface PIModeledBannerProps {
  useModeledPi: boolean;
  modeledPiPercent: number;
  onToggleModeled: (useModeled: boolean) => void;
  isUpdating?: boolean;
  /** When true (e.g. locked year), the modeled/calculator toggle is disabled. */
  disabled?: boolean;
}

export function PIModeledBanner({
  useModeledPi,
  modeledPiPercent,
  onToggleModeled,
  isUpdating = false,
  disabled = false,
}: PIModeledBannerProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleToggleClick = () => {
    if (useModeledPi) {
      // Switching from modeled to calculator - show confirmation
      setShowConfirmDialog(true);
    } else {
      // Switching from calculator to modeled - direct toggle
      onToggleModeled(true);
    }
  };

  const handleConfirmSwitch = () => {
    onToggleModeled(false);
    setShowConfirmDialog(false);
  };

  if (!useModeledPi) {
    return (
      <Alert className="border-[#4a6a8f]/30 bg-[#5a7a9f]/5">
        <AlertCircle className="h-4 w-4 text-[#5a7a9f]" />
        <AlertTitle className="text-[#4a6a8f]">PI Calculator Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-[#5a7a9f]">
            <p>
              Your PI score is currently being calculated from the measure selections below.
            </p>
            <p className="text-sm mt-1">
              To use a modeled value instead, switch using the toggle.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5a7a9f]">Modeled</span>
              <Switch
                checked={!useModeledPi}
                onCheckedChange={() => handleToggleClick()}
                disabled={isUpdating || disabled}
              />
              <span className="text-sm text-[#5a7a9f]">Calculator</span>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <Alert className="border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-700">Modeled PI Score Active</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <div className="text-amber-600">
            <p>
              Your PI score is currently set to a modeled value of{' '}
              <span className="font-semibold">{modeledPiPercent}%</span> (configured in Settings).
            </p>
            <p className="text-sm mt-1">
              To use actual PI data, switch to the PI Calculator below.
            </p>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-amber-600">Modeled</span>
              <Switch
                checked={!useModeledPi}
                onCheckedChange={() => handleToggleClick()}
                disabled={isUpdating || disabled}
              />
              <span className="text-sm text-amber-600">Calculator</span>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to PI Calculator?</DialogTitle>
            <DialogDescription>
              You are about to switch from using a modeled PI score to the PI Calculator.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground mt-2 space-y-1">
              <li>Replace your modeled PI score ({modeledPiPercent}%) with calculated values</li>
              <li>Require you to enter actual PI measure data</li>
              <li>Update your MIPS score based on the calculated PI score</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              You can switch back to modeled scoring at any time from this page or the Configuration page.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSwitch} disabled={isUpdating}>
              Switch to Calculator
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

